/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-unused-expressions*/

import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Form, Formik } from "formik";

import { GlobalAuthContext } from "store/context";
import { LoaderContext } from '@fitinsur/contexts';
import { useTextMessage } from '@fitinsur/hooks';
import { LineLabel } from './styles';

import { useGetAdminAccessGroupList } from "@fitinsur/services/AdminService";
import { useAccessGroupClone, useAccessGroupExpire, useAccessGroupRestore } from "@fitinsur/services/AccessGroupService";
import Colors from "constants/Colors";

import {
  Button,
  ButtonWithIcon,
  Text,
  ResponsiveTable,
  ListHeader,
  ListActions,
  ModalConfirmation
} from '@fitinsur/components';

import * as S from "./styles";

export function AccessGroupList({ permissions }) {
  const { getDescription } = useTextMessage();
  const { updateIsLoadingStatus } = useContext(LoaderContext);
  const authContext = useContext(GlobalAuthContext);

  const history = useHistory();
  const formik = useRef();

  const columns = [
    {
      title: getDescription("DOMAINS", 795, { textFormat: 'uppercase' }),
      key: "groupName",
      sortKey: 'groupName',
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) =>
        <LineLabel>
          {`${listItem.idGroup} - ${listItem.groupName}`}
        </LineLabel>
    },
    {
      title: getDescription("DOMAINS", 781, { textFormat: 'uppercase' }),
      key: "isExternalAccessGroup",
      sortKey: 'isExternalAccessGroup',
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) => <LineLabel>
        {listItem.isExternalAccessGroup ? getDescription("DOMAINS", 800) : getDescription("DOMAINS", 801)}
      </LineLabel>
    },
    {
      title: getDescription("DOMAINS", 799, { textFormat: 'uppercase' }),
      key: "operationAmount",
      sortKey: 'operationAmount',
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) => <LineLabel>
        {listItem.operationAmount}
      </LineLabel>
    },
    {
      title: getDescription("DOMAINS", 836, { textFormat: 'uppercase' }),
      key: "hasGroupParent",
      sortKey: 'hasGroupParent',
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) => <LineLabel>
        {listItem.idGroupParent ? getDescription("DOMAINS", 862) : getDescription("DOMAINS", 861)}
      </LineLabel>
    },
    {
      title: getDescription("DOMAINS", 796, { textFormat: 'uppercase' }),
      key: "roleAmount",
      sortKey: 'roleAmount',
      minWidth: 14,
      maxWidth: 14,
    },
    {
      title: getDescription("DOMAINS", 797, { textFormat: 'uppercase' }),
      key: "userAmount",
      sortKey: 'userAmount',
      minWidth: 14,
      maxWidth: 14,
    },
    {
      title: getDescription("DOMAINS", 783, { textFormat: 'uppercase' }),
      key: "action",
      minWidth: 8,
      maxWidth: 8,
      paddingRight: 5,
      component: (listItem) => <ListActions
        viewRoute={listItem.accessGroupExpireDate ? () => null : () => history.push(`/management/58/access-group/view/${listItem.idGroup}`)}
        editRoute={listItem.accessGroupExpireDate ? () => null : () => history.push(`/management/58/access-group/edit/${listItem.idGroup}`)}
        setShowModalConfirmationClone={() => {
          setCurrentGroup(listItem.idGroup);
          setShowModalConfirmationClone(true);
        }}
        setShowModalConfirmationExpire={() => {
          setCurrentGroup(listItem.idGroup);
          setIsRestore(listItem.accessGroupExpireDate);
          setShowModalConfirmationExpire(true);
        }}
        expired={listItem.accessGroupExpireDate}
      />
    },
  ];

  const initialFilterData = [
    { label: getDescription("DOMAINS", 795, { textFormat: 'uppercase' }), orderKey: 'groupName', key: 'groupName', type: 'text' },
    { label: getDescription("DOMAINS", 781, { textFormat: 'uppercase' }), orderKey: 'isExternalAccessGroup', key: 'isExternalAccessGroup', type: 'list' },
    { label: getDescription("DOMAINS", 799, { textFormat: 'uppercase' }), orderKey: 'operationAmount', key: 'operationAmount', type: 'text' },
    { label: getDescription("DOMAINS", 836, { textFormat: 'uppercase' }), orderKey: 'hasGroupParent', key: 'hasGroupParent', type: 'list' }
  ];

  const storedFilterOption = localStorage.getItem('access-group-filter');
  const storedStringFilterOption = localStorage.getItem('str-access-group-filter');
  let quotesUrl = '/management/58/access-group' + (storedStringFilterOption ? '?' + storedStringFilterOption : '');

  const [filterData, setFilterData] = useState(storedFilterOption ? JSON.parse(storedFilterOption) : initialFilterData);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [listData, setListData] = useState([]);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [isRestore, setIsRestore] = useState(null);
  const [showModalConfirmationClone, setShowModalConfirmationClone] = useState(false);
  const [showModalConfirmationExpire, setShowModalConfirmationExpire] = useState(false);
  const [idOperationSelected, setIdOperationSelected] = useState(null);
  const [paginationControls, setPaginationControls] = useState({
    "currentPage": 1,
    "numberMaxRecords": null,
    "total": null,
    "totalPages": null,
    "numberRecords": null
  });
  const [sortBy, setSortBy] = useState({
    key: null,
    direction: null,
  });

  const initialValues = {
    filter: {
      number: "",
      firstName: "",
      fullName: "",
      hierarchy: "",
      parent: "",
      holding: "",
      personStatus: "",
    },
  };

  const {
    loading: useGetAdminAccessGroupListLoading,
    request: useGetAdminAccessGroupListRequest,
  } = useGetAdminAccessGroupList();

  const {
    loading: useAccessGroupCloneLoading,
    status: useAccessGroupCloneStatus,
    request: useAccessGroupCloneRequest,
  } = useAccessGroupClone(currentGroup);

  const {
    loading: useAccessGroupExpireLoading,
    status: useAccessGroupExpireStatus,
    request: useAccessGroupExpireRequest,
  } = useAccessGroupExpire(currentGroup);

  const {
    loading: useAccessGroupRestoreLoading,
    status: useAccessGroupRestoreStatus,
    request: useAccessGroupRestoreRequest,
  } = useAccessGroupRestore(currentGroup);

  async function getAccessGroupListData(resetFilter) {
    const refFilterData = resetFilter ? initialFilterData : filterData;
    useGetAdminAccessGroupListRequest(null, {
      page: paginationControls.currentPage,
      sortBy,
      ...(refFilterData
        .filter(v => v.value)
        .reduce((a, b) => {
          a[b.key] = b.value;
          return a
        }, {}))
    }).then((resp) => {
      const { data: { value: { groups, pagination } } } = resp;

      if (paginationControls.currentPage === 1) {
        setListData(groups);
      } else {
        setListData([...listData, ...groups]);
        setCurrentPage(paginationControls.currentPage);
      }

      setTotalPages(pagination?.totalPages);
    });
  }

  function handleSortList(key, direction) {
    if (!key || key === 'action' || key === 'status') return;
    direction = direction === 1 ? 'DESC' : 'ASC';
    let sort = { key, direction };
    setSortBy(sort);
    let e = paginationControls;
    e.currentPage = 1;
    setPaginationControls(e);
    getAccessGroupListData();
  }

  function handleFilter(resetFilter) {
    quotesUrl = '/management/58/access-group' + (localStorage.getItem('access-group-filter') ? '?' + localStorage.getItem('str-access-group-filter') : '')
    let e = paginationControls;
    e.currentPage = 1;
    setPaginationControls(e);

    if (resetFilter) {
      getAccessGroupListData(resetFilter);
      localStorage.removeItem('access-group-filter')
      localStorage.removeItem('str-access-group-filter')
      history.push(`/management/58/access-group`)
    } else {
      getAccessGroupListData();
      history.push(`${quotesUrl}`)
    }
  };

  function setNextPage(paginationControls) {
    let e = paginationControls;
    e.currentPage++;
    setPaginationControls(e);
    getAccessGroupListData();
  }

  useEffect(() => {
    getAccessGroupListData();
  }, []);


  useEffect(() => {
    updateIsLoadingStatus(
      useGetAdminAccessGroupListLoading ||
      useAccessGroupCloneLoading ||
      useAccessGroupExpireLoading ||
      useAccessGroupRestoreLoading
    );
  }, [
    useGetAdminAccessGroupListLoading,
    useAccessGroupCloneLoading,
    useAccessGroupExpireLoading,
    useAccessGroupRestoreLoading
  ]);

  useEffect(() => {
    if (
      useAccessGroupCloneStatus === 200
    ) {
      setShowModalConfirmationClone(false);
      setShowModalConfirmationExpire(false);
      window.location.reload();
    }
  }, [useAccessGroupCloneStatus]);

  useEffect(() => {
    if (
      useAccessGroupExpireStatus === 200
    ) {

      setListData((prev) => prev.map((item) => {
        if (item.idGroup === currentGroup) {
          item.accessGroupExpireDate = new Date();
        }
        return item;
      }));

      setShowModalConfirmationExpire(false);
    }
  }, [useAccessGroupExpireStatus]);

  useEffect(() => {
    if (
      useAccessGroupRestoreStatus === 200
    ) {

      setListData((prev) => prev.map((item) => {
        if (item.idGroup === currentGroup) {
          item.accessGroupExpireDate = null;
        }

        return item;
      }));

      setShowModalConfirmationExpire(false);
    }
  }, [useAccessGroupRestoreStatus]);

  return (
    <>
      <ButtonWithIcon
        onClick={() => history.push('/management/58/access-group/create')}
        icon={{ name: "plus-gray", width: '12px', height: '12px' }}
        title={getDescription("DOMAINS", 838)}
      />

      <S.Content>
        <ModalConfirmation
          show={showModalConfirmationClone || showModalConfirmationExpire}
          text={showModalConfirmationClone ? getDescription("DOMAINS", 794) : !isRestore ? getDescription("DOMAINS", 802) : getDescription("DOMAINS", 839)}
          showTitle={false}
          textButtonCancel={getDescription("DOMAINS", 791)}
          textButtonConfirm={getDescription("DOMAINS", 792)}
          onConfirm={() => {
            if (showModalConfirmationClone) useAccessGroupCloneRequest({ idGroup: currentGroup });
            if (showModalConfirmationExpire && !isRestore) useAccessGroupExpireRequest({ idGroup: currentGroup });
            if (isRestore) useAccessGroupRestoreRequest({ idGroup: currentGroup });
          }}
          onCancel={() => {
            setCurrentGroup(null)
            setShowModalConfirmationClone(false)
            setShowModalConfirmationExpire(false)
          }}
        />
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => handleFilter(values)}
          innerRef={formik}
        >
          {(formikProps) => (
            <Form>
              <ListHeader
                filterData={filterData}
                setFilterData={setFilterData}
                onHandleFilter={(resetFilter) => handleFilter(resetFilter)}
                listNameSpace={'access-group-filter'}
                optionsExternal={[
                  { label: getDescription("DOMAINS", 800), value: 'true' },
                  { label: getDescription("DOMAINS", 801), value: 'false' }
                ]}
                optionsGroupParent={[
                  { label:  getDescription("DOMAINS", 862), value: 'true' },
                  { label:  getDescription("DOMAINS", 861), value: 'false' }
                ]}
                listUrl='/management/58/access-group'
                isAdmin
                idDomainList={995}
                idApplication={675}
                filterNamePrefix={getDescription("DOMAINS", 1074, { textFormat: 'uppercase' })}
                defaultFilterName={getDescription("DOMAINS", 1075, { textFormat: 'uppercase' })}
              // exportList={() => exportList(location.search || undefined)}
              />

              <>
                <ResponsiveTable
                  onSortTable={handleSortList}
                  formikRef={formik?.current}
                  columns={columns}
                  data={listData}
                />

                {!listData.length && (
                  <S.ButtonContainer style={{ minHeight: '450px' }}>
                    <Text style={{
                      fontWeight: 'bold',
                      color: Colors.GRAY[1],
                      margin: 'auto',
                      cursor: 'pointer',
                      marginTop: '10px'
                    }}
                      onClick={() => { }}
                    >
                      {getDescription("DOMAINS", 786)}
                    </Text>
                  </S.ButtonContainer>
                )}
              </>
            </Form>
          )}
        </Formik>

        <S.LoadContainer>
          {!listData?.length || currentPage === totalPages ? (
            <Button forth onClick={(e) => e.preventDefault()}>
              {getDescription("DOMAINS", 784, 'uppercase')}
            </Button>
          ) : (
            <Button forth onClick={() => setNextPage(paginationControls)}>
              + {getDescription("DOMAINS", 785, 'uppercase')}
            </Button>
          )}
        </S.LoadContainer>
      </S.Content>
    </>
  );
}
